import i18n from 'i18next';
import routes from '../../constants/routes';
import { allItemsSortFunc, shortItemsSortFunc } from './base-search-result.helpers';

type Filters = {
    props: {
        allItemsSortFunc?: SortFunc;
        expanded?: boolean;
        noShowAll?: boolean;
        searchKey?: string;
        shortItemsSortFunc?: SortFunc;
        title?: string;
    };
    dataKey?: string;
    filterTitle?: string;
    forbiddenUrls?: string[];
};

export type SortingOption = {
    id: string | null;
    title: string;
};

export enum SEARCH_VERSIONS {
    DETECTUM = 0,
    ELASTIC = 1,
    ELASTIC_28 = 2,
}

export enum MATERIAL_VIEWS {
    GRID = 'grid',
    LIST = 'list'
}

export const MATERIAL_IDS_START_RANGE = 10_000;

export enum PRICE_FILTER_IDS {
    FREE = 3,
    LESS_THAN_2 = 0,
    FROM_2_TO_5 = 1,
    MORE_THAN_5 = 2
}

export enum FORMAT_FILTER_IDS {
    DOWNLOADABLE = 1,
    INTERACTIVE = 0,
    INTERACTIVE_SELF_CREATED = 2,
    INTERACTIVE_PURCHASED_CUSTOMIZED = 3,
    INTERACTIVE_PURCHASED = 4,
    INTERACTIVE_STANDALONE = 5,
    INTERACTIVE_HYBRID = 6
}

export enum OWNED_BY {
    ME = 2,
    SCHOOL = 1
}

export enum TOTAL_PAGES_IDS {
    LESS_THAN_5 = 0,
    FROM_5_TO_20 = 1,
    MORE_THAN_20 = 2
}

export enum DEFAULT_SORTING_KEYS {
    CREATED_AT_DESC = 'createdAt-desc',
    FEATURED_DESC = 'authorFeatured-desc',
    PRICE_ASC = 'price-asc',
    PRICE_DESC = 'price-desc'
}

export type DefaultFiltersStateType = {
    file_types: Filter[];
    format: Filter[];
    price: Filter[];
    school_types: Filter[];
    tags: Filter[];
    class_grade: Filter[];
    learning_years: Filter[];
    material_type: Filter[];
    ownedBy: Filter[];
    taxonomyFilters: TaxonomyFilter[];
    taxonomyParents: TaxonomyFilter[];
    total_pages: number[];
};

export const DEFAULT_FILTERS_STATE: DefaultFiltersStateType = {
    class_grade: [],
    file_types: [],
    format: [],
    learning_years: [],
    material_type: [],
    price: [],
    school_types: [],
    tags: [],
    taxonomyFilters: [],
    taxonomyParents: [],
    total_pages: [],
    ownedBy: []
};

export enum SEARCH_KEYS {
    CLASS_GRADES = 'c',
    FILE_TYPES = 'ft',
    FOLDER = 'folder',
    FORMAT = 'f',
    LANGUAGES = 'lng',
    LEARNING_YEARS = 'ly',
    MATERIAL_TYPES = 'mt',
    OWNED_BY = 'ob',
    PAGE = 'p',
    PREVENT_AUTOSUGGEST = 'pas',
    PRICE = 'prc',
    QUERY = 'query',
    SALE = 'sale',
    SCHOOL_TYPES = 'st',
    SORTING = 's',
    TAGS = 'tg',
    TAXONOMY = 't',
    TOTAL_PAGES = 'tp'
}

export const MULTIPLE_SELECTION_FILTERS: string[] = [
    SEARCH_KEYS.PRICE,
    SEARCH_KEYS.FORMAT,
    SEARCH_KEYS.OWNED_BY,
    SEARCH_KEYS.CLASS_GRADES,
    SEARCH_KEYS.MATERIAL_TYPES,
    SEARCH_KEYS.FILE_TYPES,
    SEARCH_KEYS.SCHOOL_TYPES,
    SEARCH_KEYS.LEARNING_YEARS,
    SEARCH_KEYS.TOTAL_PAGES,
    SEARCH_KEYS.SALE,
    SEARCH_KEYS.TAGS,
    SEARCH_KEYS.LANGUAGES
];

export const analyticsAction = {
    [SEARCH_KEYS.PRICE]: 'setFilterPreis',
    [SEARCH_KEYS.TAGS]: 'setFilterTags',
    [SEARCH_KEYS.SALE]: 'setFilterSALE',
    [SEARCH_KEYS.CLASS_GRADES]: 'setFilterGrade',
    [SEARCH_KEYS.MATERIAL_TYPES]: 'setFilterType',
    [SEARCH_KEYS.FILE_TYPES]: 'setFilterFileType',
    [SEARCH_KEYS.TOTAL_PAGES]: 'setFilterTotalPages',
    [SEARCH_KEYS.SCHOOL_TYPES]: 'setFilterSchoolTypes',
    [SEARCH_KEYS.LEARNING_YEARS]: 'setFilterLearningYears',
    [SEARCH_KEYS.LANGUAGES]: 'setFilterLanguages'
};

export const FORBIDDEN_URLS_FOR_SUBJECTS = [
    routes.SCHOOL_LANDING_PAGES.prefix,
    routes.SCHOOL_LANDING_PAGES.path,
    routes.SCHOOL_LANDING_PAGES.fullpath
];

export const FORBIDDEN_URLS_FOR_SUBJECTS_WITH_SEARCH = [
    routes.SCHOOL_LANDING_PAGES.prefix,
    routes.SCHOOL_LANDING_PAGES.path,
    routes.SCHOOL_LANDING_PAGES.fullpath,
    routes.SEARCH_RESULT.path
];

let filters: Filters[];

const translatedFilters = () => [
    {
        dataKey: 'tags',
        forbiddenUrls: [
            routes.SEARCH_RESULT.path,
            routes.FAVORITES.path,
            routes.BOUGHT_MATERIALS.path,
            routes.LANDING.path,
            routes.SCHOOL_LANDING_PAGES.prefix,
            routes.SCHOOL_LANDING_PAGES.path,
            routes.SCHOOL_LANDING_PAGES.fullpath,
            routes.SCHOOL_WISHLIST.path,
            routes.NEW_INTERACTIVE_MATERIALS.path,
            routes.NEW_INTERACTIVE_MY_MATERIALS.path
        ],
        props: {
            allItemsSortFunc,
            searchKey: SEARCH_KEYS.TAGS,
            shortItemsSortFunc,
            title: `${i18n.t('search.filters.tags')}`
        }
    },
    {
        dataKey: 'price',
        filterTitle: 'Preisfilter',
        forbiddenUrls: [routes.BOUGHT_MATERIALS.path],
        props: {
            noShowAll: true,
            searchKey: SEARCH_KEYS.PRICE,
            title: `${i18n.t('search.filters.price')}`
        }
    },
    {
        dataKey: 'languages',
        props: {
            noShowAll: true,
            searchKey: SEARCH_KEYS.LANGUAGES,
            title: `${i18n.t('search.filters.language')}`
        }
    },
    {
        dataKey: 'class_grade',
        filterTitle: 'Klassenfilter',
        props: {
            noShowAll: true,
            searchKey: SEARCH_KEYS.CLASS_GRADES,
            title: `${i18n.t('search.filters.classGrade')}`
        }
    },
    {
        dataKey: 'learning_years',
        props: {
            noShowAll: true,
            searchKey: SEARCH_KEYS.LEARNING_YEARS,
            title: `${i18n.t('search.filters.learningYears')}`
        }
    },
    {
        dataKey: 'material_type',
        filterTitle: 'Materialtypfilter',
        props: {
            allItemsSortFunc,
            searchKey: SEARCH_KEYS.MATERIAL_TYPES,
            shortItemsSortFunc,
            title: `${i18n.t('search.filters.materialType')}`
        }
    },
    {
        dataKey: 'total_pages',
        props: {
            noShowAll: true,
            searchKey: SEARCH_KEYS.TOTAL_PAGES,
            title: `${i18n.t('search.filters.totalPages')}`
        }
    }
];

export const getDefaultSortingOptions = (isEmpty: boolean, isPopular = true): SortingOption[] => {
    const options = [
        {
            id: DEFAULT_SORTING_KEYS.PRICE_ASC,
            title: i18n.t('search.results.sorting.priceAscending')
        },
        {
            id: DEFAULT_SORTING_KEYS.PRICE_DESC,
            title: i18n.t('search.results.sorting.priceDescending')
        },
        {
            id: DEFAULT_SORTING_KEYS.CREATED_AT_DESC,
            title: i18n.t('search.results.sorting.new')
        }
    ];

    if (isPopular && isEmpty) {
        options.push({
            id: null as unknown as DEFAULT_SORTING_KEYS,
            title: i18n.t('search.results.sorting.popularMaterials')
        });
    }

    if (!isEmpty) {
        options.push({
            id: null as unknown as DEFAULT_SORTING_KEYS,
            title: i18n.t('search.results.sorting.bestResults')
        });
    }

    return options;
};

export const getWishlistSortingOptions = (isEmpty: boolean) => {
    return getDefaultSortingOptions(isEmpty, false);
};

export const filtersList = () => {
    if (!filters) {
        filters = translatedFilters();

        return filters;
    }

    return filters;
};

export const moreItemsLimit = 5;
